import axios from "axios";

// Util for storing resource IDs in browser or Node (uses localStorage if available)
const store = {
    get(key: string): string | null {
        if (typeof window !== "undefined" && window.localStorage) return window.localStorage.getItem(key);
        if ((global as any).__erp_store) return (global as any).__erp_store[key] || null;
        return null;
    },
    set(key: string, value: string) {
        if (typeof window !== "undefined" && window.localStorage) window.localStorage.setItem(key, value);
        else {
            (global as any).__erp_store = (global as any).__erp_store || {};
            (global as any).__erp_store[key] = value;
        }
    },
    remove(key: string) {
        if (typeof window !== "undefined" && window.localStorage) window.localStorage.removeItem(key);
        else if ((global as any).__erp_store) delete (global as any).__erp_store[key];
    }
};
const supportedUnits = [
    "קרטון", "ארגז", "ק''ג", "יח'", "משטח", "כללי", "דלי", "שק",
    "ליטר", "מנה", "קופסא", "שקיות", "צרור", "נספק", "מיכל", "פאקט", "מארז", "רוחב", "אורך", "שטח"
];
type Product = { id: string; name: string; barcode?: string; [key: string]: any; };

export async function extractProductsWithAssistant2(
    text: string,
    apiKey: string,
    productList: Product[]
): Promise<any[]> {
    const FILE_KEY = "openai_product_file_id";
    const VECTOR_STORE_KEY = "openai_product_vector_store_id";
    const ASSISTANT_KEY = "openai_product_assistant_id";
    const THREAD_KEY = "openai_product_thread_id";
    const openaiBaseV1 = "https://api.openai.com/v1";
    console.log(productList)
    // 1. Upload file if not already uploaded
    let fileId = store.get(FILE_KEY);
    if (!fileId) {
        const blob = new Blob([JSON.stringify(productList)], { type: "application/json" });
        const formData = new FormData();
        formData.append("purpose", "assistants");
        formData.append("file", blob, "products.json");
        const fileResp = await axios.post(
            `${openaiBaseV1}/files`,
            formData,
            { headers: { "Authorization": `Bearer ${apiKey}` } }
        );
        fileId = fileResp.data.id;
        store.set(FILE_KEY, fileId);
    }

    // 2. Create or find vector store with this file
    let vectorStoreId = store.get(VECTOR_STORE_KEY);
    let found = false;
    if (vectorStoreId) {
        // check if this store still exists and has the file
        try {
            const resp = await axios.get(
                `${openaiBaseV1}/vector_stores/${vectorStoreId}`,
                { headers: { "Authorization": `Bearer ${apiKey}` } }
            );
            // Need to check files in the vector store (separate endpoint)
            const filesResp = await axios.get(
                `${openaiBaseV1}/vector_stores/${vectorStoreId}/files`,
                { headers: { "Authorization": `Bearer ${apiKey}` } }
            );
            const fileIds = (filesResp.data.data || []).map((f: any) => f.id);
            found = resp.data.status !== "expired" && fileIds.includes(fileId);
        } catch { found = false; }
    }
    if (!found) {
        // List vector stores to avoid dupes
        const storesResp = await axios.get(
            `${openaiBaseV1}/vector_stores?limit=100`,
            { headers: { "Authorization": `Bearer ${apiKey}` } }
        );
        const stores = storesResp.data.data || [];
        for (const vs of stores) {
            const vsDetail = await axios.get(
                `${openaiBaseV1}/vector_stores/${vs.id}`,
                { headers: { "Authorization": `Bearer ${apiKey}` } }
            );
            const filesResp = await axios.get(
                `${openaiBaseV1}/vector_stores/${vs.id}/files`,
                { headers: { "Authorization": `Bearer ${apiKey}` } }
            );
            const fileIds = (filesResp.data.data || []).map((f: any) => f.id);
            if (vsDetail.data.status !== "expired" && fileIds.includes(fileId)) {
                vectorStoreId = vsDetail.data.id;
                store.set(VECTOR_STORE_KEY, vectorStoreId);
                found = true;
                break;
            }
        }
    }
    if (!found) {
        // Create a new vector store
        const vsResp = await axios.post(
            `${openaiBaseV1}/vector_stores`,
            { file_ids: [fileId], name: "ERP Product Vector Store" },
            { headers: { "Authorization": `Bearer ${apiKey}`, "Content-Type": "application/json" } }
        );
        vectorStoreId = vsResp.data.id;
        store.set(VECTOR_STORE_KEY, vectorStoreId);
    }

    // Wait for the vector store to be ready
    let ready = false;
    while (!ready) {
        const statusResp = await axios.get(
            `${openaiBaseV1}/vector_stores/${vectorStoreId}`,
            { headers: { "Authorization": `Bearer ${apiKey}` } }
        );
        const { file_counts } = statusResp.data;
        ready = (file_counts.in_progress === 0 && file_counts.completed === file_counts.total);
        if (!ready) await new Promise(r => setTimeout(r, 1500));
    }

    // 3. Create or get assistant with file_search pointing to this vector store (force JSON response)
    let assistantId = store.get(ASSISTANT_KEY);
    let needsNewAssistant = false;
    if (assistantId) {
        try {
            const asstResp = await axios.get(
                `${openaiBaseV1}/assistants/${assistantId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${apiKey}`,
                        "OpenAI-Beta": "assistants=v2"
                    }
                }
            );
            // Check if the assistant has the right vector store attached
            const vsIds = asstResp.data.tool_resources?.file_search?.vector_store_ids || [];
            if (!vsIds.includes(vectorStoreId)) needsNewAssistant = true;
        } catch { needsNewAssistant = true; }
    } else {
        needsNewAssistant = true;
    }

    if (needsNewAssistant) {
        const instructions = `
You are given a product database as a JSON file. Each product has: id, product_name, nicknames (array), and other fields.

When matching products from a user's request (e.g., 'לחם 3 קרטון, בצל 2 ק"ג'):
- Always try to match the user text to the product_name first (case-insensitive, exact or close match).
- Only if there is no good match with product_name, use nicknames for matching.
- Ignore the unit for matching purposes.
- Recognize these possible units: ${supportedUnits.join(", ")}.
- Respond ONLY with a minified JSON array: [{id,user_product_text,score,quantity,unit}], where unit=null if not provided. No extra fields, explanations, markdown, or code blocks.
- Score is 0..1, where 1 is a perfect match.
- Example: [{"id":1,"user_product_text":"לחם","score":1,"quantity":3,"unit":"קרטון"}]
`
            .trim();
        const assistantResp = await axios.post(
            `${openaiBaseV1}/assistants`,
            {
                name: "Product Matcher",
                instructions:
                instructions ,
                tools: [{ type: "file_search" }],
                tool_resources: { file_search: { vector_store_ids: [vectorStoreId] } },
                model: "gpt-4o",
                response_format: { type: "json_object" }
            },
            {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "Content-Type": "application/json",
                    "OpenAI-Beta": "assistants=v2"
                }
            }
        );

        assistantId = assistantResp.data.id;
        store.set(ASSISTANT_KEY, assistantId);
    }

    // 4. Use or create a single thread for the session
    let threadId = store.get(THREAD_KEY);
    if (!threadId) {
        const threadResp = await axios.post(
            `${openaiBaseV1}/threads`,
            {},
            {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "OpenAI-Beta": "assistants=v2"
                }
            }
        );
        threadId = threadResp.data.id;
        store.set(THREAD_KEY, threadId);
    }

    // 5. Add user message
    await axios.post(
        `${openaiBaseV1}/threads/${threadId}/messages`,
        { role: "user", content: text },
        {
            headers: {
                "Authorization": `Bearer ${apiKey}`,
                "Content-Type": "application/json",
                "OpenAI-Beta": "assistants=v2"
            }
        }
    );

    // 6. Run assistant
    const runResp = await axios.post(
        `${openaiBaseV1}/threads/${threadId}/runs`,
        { assistant_id: assistantId },
        {
            headers: {
                "Authorization": `Bearer ${apiKey}`,
                "Content-Type": "application/json",
                "OpenAI-Beta": "assistants=v2"
            }
        }
    );
    const runId = runResp.data.id;

    // 7. Poll run status
    let runStatus = "";
    do {
        const statusResp = await axios.get(
            `${openaiBaseV1}/threads/${threadId}/runs/${runId}`,
            {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "OpenAI-Beta": "assistants=v2"
                }
            }
        );
        runStatus = statusResp.data.status;
        if (runStatus !== "completed") await new Promise(r => setTimeout(r, 2000));
    } while (runStatus !== "completed");

    // 8. Get the latest assistant's response (should be just the JSON)
    const messagesResp = await axios.get(
        `${openaiBaseV1}/threads/${threadId}/messages`,
        {
            headers: {
                "Authorization": `Bearer ${apiKey}`,
                "OpenAI-Beta": "assistants=v2"
            }
        }
    );
    const allMsgs = messagesResp.data.data || [];
    const assistantMsgs = allMsgs.filter((msg: any) => msg.role === "assistant");
    if (!assistantMsgs.length) return [{ error: "No assistant reply found" }];
    const lastMsg = assistantMsgs[0];
    // If assistant returned a list (as object), take the content (should be JSON!)
    try {
        // May come as array, object, or string
        let val = lastMsg.content?.[0]?.text?.value || lastMsg.content?.[0]?.value || lastMsg.content?.[0] || lastMsg.content || "";
        return cleanJsonAssistantReply(val);
    } catch (e) {
        return [{ error: "Could not parse JSON from assistant reply", content: lastMsg }];
    }
}
function cleanJsonAssistantReply(val: string): any {
    // Remove markdown code block if present
    let txt = val.trim();
    if (txt.startsWith("```json")) txt = txt.replace(/^```json/, "");
    if (txt.endsWith("```")) txt = txt.replace(/```$/, "");
    // Remove all line breaks and excess whitespace
    txt = txt.replace(/[\r\n\t]+/g, "");
    txt = txt.trim();
    // Only keep the first JSON array in the string
    const match = txt.match(/\[.*\]/);
    if (match) txt = match[0];
    try {
        return JSON.parse(txt);
    } catch (e) {
        return [{ error: "Could not parse clean JSON", content: val }];
    }
}

export async function extractProductsFromPdfAssistant(
    file: File,
    apiKey: string,
    productList: Product[]
): Promise<any[]> {
    const PDF_ASSISTANT_KEY = "openai_product_pdf_assistant_id";
    const PDF_THREAD_KEY = "openai_product_pdf_thread_id";
    const openaiBaseV1 = "https://api.openai.com/v1";

    // 1. Upload the PDF file to OpenAI
    const pdfFormData = new FormData();
    pdfFormData.append("purpose", "assistants");
    pdfFormData.append("file", file, file.name);
    const pdfFileResp = await axios.post(
        `${openaiBaseV1}/files`,
        pdfFormData,
        { headers: { "Authorization": `Bearer ${apiKey}` } }
    );
    const pdfFileId = pdfFileResp.data.id;

    // 2. Reuse or create the code-interpreter assistant for PDFs
    let assistantId = store.get(PDF_ASSISTANT_KEY);
    let needsNewAssistant = false;
    if (assistantId) {
        try {
            await axios.get(
                `${openaiBaseV1}/assistants/${assistantId}`,
                {
                    headers: {
                        "Authorization": `Bearer ${apiKey}`,
                        "OpenAI-Beta": "assistants=v2"
                    }
                }
            );
        } catch { needsNewAssistant = true; }
    } else {
        needsNewAssistant = true;
    }

    if (needsNewAssistant) {
        // Build instructions for PDF extraction
        const instructions = `
You are given a product database as a JSON file. Each product has: id, product_name, nicknames (array), and other fields.
Given a PDF file containing a product order, extract all product lines as a minified JSON array. 


When matching products from a user's request (e.g., 'לחם 3 קרטון, בצל 2 ק"ג'):
- Always try to match the user text to the product_name first (case-insensitive, exact or close match).
- Only if there is no good match with product_name, use nicknames for matching.
- Ignore the unit for matching purposes.
- Recognize these possible units: ${supportedUnits.join(", ")}.
- Respond ONLY with a minified JSON array: [{id,user_product_text,score,quantity,unit}], where unit=null if not provided. No extra fields, explanations, markdown, or code blocks.
- Score is 0..1, where 1 is a perfect match.
- Example: [{"id":1,"user_product_text":"לחם","score":1,"quantity":3,"unit":"קרטון"}]
`
            .trim();

        const assistantResp = await axios.post(
            `${openaiBaseV1}/assistants`,
            {
                name: "Product PDF Extractor",
                instructions,
                tools: [{ type: "code_interpreter" }],
                model: "gpt-4o",
                response_format: { type: "json_object" }
            },
            {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "Content-Type": "application/json",
                    "OpenAI-Beta": "assistants=v2"
                }
            }
        );
        assistantId = assistantResp.data.id;
        store.set(PDF_ASSISTANT_KEY, assistantId);
    }

    // 3. Reuse or create a thread for PDF extraction
    let threadId = store.get(PDF_THREAD_KEY);
    if (!threadId) {
        const threadResp = await axios.post(
            `${openaiBaseV1}/threads`,
            {},
            {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "OpenAI-Beta": "assistants=v2"
                }
            }
        );
        threadId = threadResp.data.id;
        store.set(PDF_THREAD_KEY, threadId);
    }

    // 4. Add a user message to the thread (the PDF is already uploaded)
    await axios.post(
        `${openaiBaseV1}/threads/${threadId}/messages`,
        {
            role: "user",
            content:
                `The attached PDF contains a list of products and quantities. ` +
                `Extract each line as a JSON object with user_product_text, quantity, unit. ` +
                `Recognize these units: ${supportedUnits.join(", ")}. Return ONLY a minified JSON array, no explanations or markdown.`
        },
        {
            headers: {
                "Authorization": `Bearer ${apiKey}`,
                "Content-Type": "application/json",
                "OpenAI-Beta": "assistants=v2"
            }
        }
    );

    // 5. Run the assistant, passing the PDF as a code interpreter tool resource
    const runResp = await axios.post(
        `${openaiBaseV1}/threads/${threadId}/runs`,
        {
            assistant_id: assistantId,
            tool_resources: { code_interpreter: { file_ids: [pdfFileId] } }
        },
        {
            headers: {
                "Authorization": `Bearer ${apiKey}`,
                "Content-Type": "application/json",
                "OpenAI-Beta": "assistants=v2"
            }
        }
    );
    const runId = runResp.data.id;

    // 6. Poll run status until complete
    let runStatus = "";
    do {
        const statusResp = await axios.get(
            `${openaiBaseV1}/threads/${threadId}/runs/${runId}`,
            {
                headers: {
                    "Authorization": `Bearer ${apiKey}`,
                    "OpenAI-Beta": "assistants=v2"
                }
            }
        );
        runStatus = statusResp.data.status;
        if (runStatus !== "completed") await new Promise(r => setTimeout(r, 2000));
    } while (runStatus !== "completed");

    // 7. Get the latest assistant's response (should be minified JSON)
    const messagesResp = await axios.get(
        `${openaiBaseV1}/threads/${threadId}/messages`,
        {
            headers: {
                "Authorization": `Bearer ${apiKey}`,
                "OpenAI-Beta": "assistants=v2"
            }
        }
    );
    const allMsgs = messagesResp.data.data || [];
    const assistantMsgs = allMsgs.filter((msg: any) => msg.role === "assistant");
    if (!assistantMsgs.length) return [{ error: "No assistant reply found" }];
    const lastMsg = assistantMsgs[0];

    try {
        let val = lastMsg.content?.[0]?.text?.value || lastMsg.content?.[0]?.value || lastMsg.content?.[0] || lastMsg.content || "";
        return cleanJsonAssistantReply(val);
    } catch (e) {
        return [{ error: "Could not parse JSON from assistant reply", content: lastMsg }];
    }
}
